import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Icon from '../../components/AppIcon';
import SEO from '../../components/SEO';
import { supabase } from '../../lib/supabase';

const SignUp = () => {
  const navigate = useNavigate();
  const { signUp, user } = useAuth();
  
  // Clear demo mode on mount
  useEffect(() => {
    localStorage.removeItem('demo_mode');
  }, []);

  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    companyName: '',
    password: '',
    confirmPassword: '',
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [generalError, setGeneralError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Redirect if already logged in
  useEffect(() => {
    if (user) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData?.fullName?.trim()) {
      newErrors.fullName = 'Full name is required';
    } else if (formData?.fullName?.trim()?.length < 2) {
      newErrors.fullName = 'Full name must be at least 2 characters';
    }
    
    if (!formData?.email?.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    if (!formData?.companyName?.trim()) {
      newErrors.companyName = 'Company name is required';
    }
    
    if (!formData?.password) {
      newErrors.password = 'Password is required';
    } else if (formData?.password?.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/?.test(formData?.password)) {
      newErrors.password = 'Password must contain uppercase, lowercase, and number';
    }
    
    if (!formData?.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData?.password !== formData?.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e?.target || {};
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error for this field when user starts typing
    if (errors?.[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
    // Clear general error
    if (generalError) {
      setGeneralError('');
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    setGeneralError('');
    
    try {
      // Call Edge Function to create Stripe checkout session
      const { data, error } = await supabase?.functions?.invoke('create-subscription', {
        body: {
          email: formData?.email,
          fullName: formData?.fullName,
          companyName: formData?.companyName,
          password: formData?.password,
          role: 'admin',
          appUrl: window.location?.origin,
        }
      });

      if (error) {
        throw new Error(error?.message || 'Failed to connect to subscription service. Please try again.');
      }

      if (data?.error) {
        throw new Error(data.error);
      }

      // Redirect to Stripe Checkout
      if (data?.url) {
        window.location.href = data?.url;
      } else {
        throw new Error('No checkout URL received. Please try again.');
      }
    } catch (error) {
      console.error('Sign up error:', error);
      setGeneralError(error?.message || 'An error occurred during sign up. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4 py-8">
      <SEO
        title="Start Free Trial — ADU Flow CRM for Construction Companies"
        description="Create your free ADU Flow account. Get started with ADU construction project management, permit tracking, contractor management, and customer CRM — free trial, no credit card required."
        keywords="ADU CRM free trial, ADU construction software signup, ADU project management software"
        canonical="/sign-up"
      />
      <div className="w-full max-w-2xl">
        {/* Logo and Branding */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <img
              src="/assets/images/adu_flow_logo.png"
              alt="ADU Flow logo - ADU Construction CRM"
              className="h-16 w-auto object-contain"
            />
          </div>
          <h1 className="text-3xl font-bold text-foreground mb-2">
            ADU Flow
          </h1>
          <p className="text-muted-foreground">
            Create your account to get started
          </p>
        </div>

        {/* Sign Up Form Card */}
        <div className="bg-card rounded-lg shadow-lg p-8 border border-border">
          <h2 className="text-2xl font-semibold text-card-foreground mb-6">
            Create Account
          </h2>

          {/* General Error Message */}
          {generalError && (
            <div className="mb-6 p-4 bg-error/10 border border-error/20 rounded-md flex items-start gap-3">
              <Icon name="AlertCircle" size={20} className="text-error flex-shrink-0 mt-0.5" />
              <p className="text-sm text-error">{generalError}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Full Name Input */}
            <Input
              type="text"
              name="fullName"
              label="Full Name"
              placeholder="John Doe"
              value={formData?.fullName}
              onChange={handleChange}
              error={errors?.fullName}
              required
              autoComplete="name"
              disabled={loading}
            />

            {/* Email Input */}
            <Input
              type="email"
              name="email"
              label="Email Address"
              placeholder="you@example.com"
              value={formData?.email}
              onChange={handleChange}
              error={errors?.email}
              required
              autoComplete="email"
              disabled={loading}
            />

            {/* Company Name Input */}
            <Input
              type="text"
              name="companyName"
              label="Company Name"
              placeholder="ADU Construction Inc."
              value={formData?.companyName}
              onChange={handleChange}
              error={errors?.companyName}
              required
            />

            {/* Password Input */}
            <div className="relative">
              <Input
                type={showPassword ? 'text' : 'password'}
                name="password"
                label="Password"
                placeholder="Create a strong password"
                value={formData?.password}
                onChange={handleChange}
                error={errors?.password}
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-9 text-muted-foreground hover:text-foreground transition-colors"
              >
                <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={20} />
              </button>
            </div>

            {/* Confirm Password Input */}
            <div className="relative">
              <Input
                type={showConfirmPassword ? 'text' : 'password'}
                name="confirmPassword"
                label="Confirm Password"
                placeholder="Re-enter your password"
                value={formData?.confirmPassword}
                onChange={handleChange}
                error={errors?.confirmPassword}
                required
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-9 text-muted-foreground hover:text-foreground transition-colors"
              >
                <Icon name={showConfirmPassword ? 'EyeOff' : 'Eye'} size={20} />
              </button>
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              fullWidth
              loading={loading}
              disabled={loading}
              className="mt-6"
            >
              {loading ? 'Creating Account...' : 'Create Account & Start Free Trial'}
            </Button>

            {/* Pricing Note */}
            <p className="text-xs text-center text-muted-foreground mt-3">
              First month free, then $29/month. No setup fees. Cancel anytime.
            </p>

            {/* Sign In Link */}
            <div className="mt-6 text-center">
              <p className="text-sm text-muted-foreground">
                Already have an account?{' '}
                <Link
                  to="/login"
                  className="text-accent hover:text-accent/80 font-medium transition-colors"
                >
                  Sign In
                </Link>
              </p>
            </div>
          </form>
        </div>

        {/* Footer */}
        <p className="text-center text-sm text-muted-foreground mt-6">
          © 2026 ADU Flow. All rights reserved.
        </p>
      </div>
    </div>
  );
};

export default SignUp;